/**
 * Authentication Service
 * Handles Google OAuth flow and JWT token management
 */

interface AuthTokens {
  accessToken: string;
  expiresAt: number;
}

interface UserInfo {
  sub: string;
  email: string;
  name: string;
  picture?: string;
}

class AuthService {
  private readonly STORAGE_KEY = 'auth_token';
  private readonly USER_INFO_KEY = 'user_info';
  private readonly API_URL = import.meta.env.VITE_API_URL || '';

  /**
   * Initiate Google OAuth login flow
   */
  async login() {
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${this.API_URL}/api/auth/google`;
  }

  /**
   * Handle OAuth callback
   */
  async handleCallback(callbackUrl: string): Promise<boolean> {
    try {
      const url = new URL(callbackUrl);
      const error = url.searchParams.get('error');

      // Check for errors
      if (error) {
        console.error('Google OAuth error:', error);
        return false;
      }

      // Get token from URL parameters (JWT token from backend)
      const token = url.searchParams.get('token');
      const expiresIn = url.searchParams.get('expires_in');

      if (token) {
        const tokens: AuthTokens = {
          accessToken: token,
          expiresAt: Date.now() + (expiresIn ? parseInt(expiresIn) * 1000 : 3600000),
        };

        this.saveTokens(tokens);
        
        // Decode JWT to get user info (without verification, just for display)
        const userInfo = this.decodeToken(token);
        if (userInfo) {
          localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
        }
        
        return true;
      }

      console.error('No token found in callback URL');
      return false;
    } catch (error) {
      console.error('Callback handling error:', error);
      return false;
    }
  }

  /**
   * Decode JWT token to extract user info
   */
  private decodeToken(token: string): UserInfo | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);
      return {
        sub: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Save tokens to localStorage
   */
  private saveTokens(tokens: AuthTokens) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tokens));
  }

  /**
   * Get stored tokens
   */
  getTokens(): AuthTokens | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return null;
    
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  /**
   * Get user info
   */
  getUserInfo(): UserInfo | null {
    // Return mock user when auth is disabled
    if (this.isAuthDisabled()) {
      return {
        sub: 'dev-user',
        email: 'dev@localhost',
        name: 'Development User',
        picture: undefined,
      };
    }

    const stored = localStorage.getItem(this.USER_INFO_KEY);
    if (!stored) return null;
    
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  /**
   * Check if authentication is disabled via environment variable
   * Set VITE_DISABLE_AUTH=true to bypass authentication
   */
  private isAuthDisabled(): boolean {
    const disableAuth = import.meta.env.VITE_DISABLE_AUTH;
    return disableAuth === 'true' || disableAuth === '1';
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    // Bypass authentication if DISABLE_AUTH is enabled
    if (this.isAuthDisabled()) {
      return true;
    }

    const tokens = this.getTokens();
    
    if (!tokens) return false;
    
    // Check if token is expired
    if (Date.now() >= tokens.expiresAt) {
      this.logout();
      return false;
    }
    
    return true;
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    // Return null when auth is disabled (backend will handle it)
    if (this.isAuthDisabled()) {
      return null;
    }

    const tokens = this.getTokens();
    return tokens?.accessToken || null;
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.USER_INFO_KEY);
  }
}

export const authService = new AuthService();
export type { AuthTokens, UserInfo };

